// Texts of the design area (GIOTHUB-185). English defines the keys.
import type { Dict } from "./dict";

const en = {
  "design.title": "Design · {name}",
  "design.intro": "Scenario B: the requirements first, confirmed by you; then the assistant designs the protocol from them.",
  "design.manual": "The steps in the manual",
  "design.print.title": "{name} - Requirements",
  "design.print.meta": "requirements {status}",
  "design.print.confirmed": "confirmed (version {version}) {when}",
  "design.print.draft": "draft, version {version}",
  "design.by.ai": "written by the assistant",
  "design.by.person": "edited by a person",
  "design.working": "The assistant is working…",
  "design.saving": "Saving…",
  "design.req.title": "1 · Requirements",
  "design.req.confirmed": "Confirmed · version {version}",
  "design.req.draft": "Draft · version {version}",
  "design.req.print": "Print / PDF",
  "design.req.print.hint": "Print, or save as PDF from the print dialog",
  "design.req.confirm.hint": "The assistant designs the protocol only from confirmed requirements",
  "design.req.confirming": "Confirming…",
  "design.req.confirm": "Confirm the requirements",
  "design.req.empty": "No requirements yet. Describe the device: what it measures and how often, what it alarms on, how it is configured, its radio and power budget. Upload schematics, datasheets and specifications under Inputs in the explorer first - the assistant reads them.",
  "design.req.placeholder": "A fill level sensor for public waste bins: ultrasonic distance every 15 minutes, report every hour over LTE-M, alarm at 80 % full, battery 5 years on 2 x AA lithium, configurable report interval and thresholds…",
  "design.req.draftai": "Draft the requirements with the assistant",
  "design.req.edit.note": "Markdown. Saving makes the requirements a draft again: confirm them after the edit.",
  "design.req.older": "Version {version} was confirmed {when}; this is a newer draft. The protocol stays as it is until you confirm again.",
  "design.req.readthem": "Read them, edit what is wrong (or tell the assistant), answer its questions in the assistant panel, then confirm. Nothing is designed before.",
  "design.proto.title": "2 · Protocol",
  "design.proto.limit": "Payload limit",
  "design.proto.bytes": "bytes",
  "design.proto.download.hint": "The .proto files and the nanopb options, for the firmware team",
  "design.proto.download": "Download for the firmware",
  "design.proto.ready": "The requirements are confirmed: the assistant can design the protocol (.proto and nanopb options) and then classify it.",
  "design.proto.after": "After you confirm the requirements.",
  "design.proto.design": "Design the protocol with the assistant",
  "design.proto.col.message": "Message",
  "design.proto.col.largest": "Largest encoding",
  "design.proto.col.notes": "Notes",
  "design.proto.root": "root",
  "design.proto.unbounded": "unbounded",
  "design.proto.over": " · over the limit",
  "design.proto.callbacks": "streamed by callback (one element counted): {fields}",
  "design.proto.nosizes": "Sizes after the manifest names the root message.",
  "design.proto.problem": "nanopb options {problem}",
  "design.proto.note": "Each string, bytes and repeated field at its nanopb limit; numbers at their largest varint. Sizes from the current draft and nanopb options.",
  "design.opts.title": "3 · nanopb options (for the firmware)",
  "design.opts.meta": "version {version} · {author} {when}",
  "design.opts.intro": "One line per string, bytes or repeated field:",
  "design.opts.kept": "Kept in the project (the store package has no place for them) and included in the firmware download.",
  "design.opts.by.ai": "written by the assistant",
  "design.opts.by.person": "edited by a person",
  "design.opts.save": "Save options",
  "design.opts.discard": "Discard",
} as const;

const de = {
  "design.title": "Design · {name}",
  "design.intro": "Szenario B: zuerst die Anforderungen, von Ihnen bestätigt; dann entwirft der Assistent daraus das Protokoll.",
  "design.manual": "Die Schritte im Handbuch",
  "design.print.title": "{name} - Anforderungen",
  "design.print.meta": "Anforderungen {status}",
  "design.print.confirmed": "bestätigt (Version {version}) {when}",
  "design.print.draft": "Entwurf, Version {version}",
  "design.by.ai": "vom Assistenten geschrieben",
  "design.by.person": "von einer Person bearbeitet",
  "design.working": "Der Assistent arbeitet…",
  "design.saving": "Wird gespeichert…",
  "design.req.title": "1 · Anforderungen",
  "design.req.confirmed": "Bestätigt · Version {version}",
  "design.req.draft": "Entwurf · Version {version}",
  "design.req.print": "Drucken / PDF",
  "design.req.print.hint": "Drucken oder im Druckdialog als PDF speichern",
  "design.req.confirm.hint": "Der Assistent entwirft das Protokoll nur aus bestätigten Anforderungen",
  "design.req.confirming": "Wird bestätigt…",
  "design.req.confirm": "Anforderungen bestätigen",
  "design.req.empty": "Noch keine Anforderungen. Beschreiben Sie das Gerät: was es misst und wie oft, wann es alarmiert, wie es konfiguriert wird, seine Funkverbindung und sein Energiebudget. Laden Sie zuerst Schaltpläne, Datenblätter und Spezifikationen unter Eingaben im Explorer hoch - der Assistent liest sie.",
  "design.req.placeholder": "Ein Füllstandssensor für öffentliche Abfallbehälter: Ultraschalldistanz alle 15 Minuten, Meldung jede Stunde über LTE-M, Alarm bei 80 % Füllung, Batterie 5 Jahre mit 2 x AA Lithium, Meldeintervall und Schwellwerte konfigurierbar…",
  "design.req.draftai": "Anforderungen mit dem Assistenten entwerfen",
  "design.req.edit.note": "Markdown. Speichern macht die Anforderungen wieder zum Entwurf: Bestätigen Sie sie nach der Bearbeitung.",
  "design.req.older": "Version {version} wurde {when} bestätigt; dies ist ein neuerer Entwurf. Das Protokoll bleibt, wie es ist, bis Sie erneut bestätigen.",
  "design.req.readthem": "Lesen Sie sie, korrigieren Sie Falsches (oder sagen Sie es dem Assistenten), beantworten Sie seine Fragen im Assistenten-Bereich und bestätigen Sie dann. Vorher wird nichts entworfen.",
  "design.proto.title": "2 · Protokoll",
  "design.proto.limit": "Payload-Grenze",
  "design.proto.bytes": "Bytes",
  "design.proto.download.hint": "Die .proto-Dateien und die nanopb-Optionen für das Firmware-Team",
  "design.proto.download": "Für die Firmware herunterladen",
  "design.proto.ready": "Die Anforderungen sind bestätigt: Der Assistent kann das Protokoll (.proto und nanopb-Optionen) entwerfen und es dann klassifizieren.",
  "design.proto.after": "Nachdem Sie die Anforderungen bestätigt haben.",
  "design.proto.design": "Protokoll mit dem Assistenten entwerfen",
  "design.proto.col.message": "Nachricht",
  "design.proto.col.largest": "Grösste Kodierung",
  "design.proto.col.notes": "Hinweise",
  "design.proto.root": "Root",
  "design.proto.unbounded": "unbegrenzt",
  "design.proto.over": " · über der Grenze",
  "design.proto.callbacks": "per Callback gestreamt (ein Element gezählt): {fields}",
  "design.proto.nosizes": "Grössen, sobald das Manifest die Root-Nachricht nennt.",
  "design.proto.problem": "nanopb-Optionen {problem}",
  "design.proto.note": "Jedes String-, Bytes- und Repeated-Feld an seiner nanopb-Grenze; Zahlen mit ihrem grössten Varint. Grössen aus dem aktuellen Entwurf und den nanopb-Optionen.",
  "design.opts.title": "3 · nanopb-Optionen (für die Firmware)",
  "design.opts.meta": "Version {version} · {author} {when}",
  "design.opts.intro": "Eine Zeile pro String-, Bytes- oder Repeated-Feld:",
  "design.opts.kept": "Im Projekt gespeichert (das Store-Paket hat keinen Platz dafür) und im Firmware-Download enthalten.",
  "design.opts.by.ai": "vom Assistenten geschrieben",
  "design.opts.by.person": "von einer Person bearbeitet",
  "design.opts.save": "Optionen speichern",
  "design.opts.discard": "Verwerfen",
} satisfies Dict<typeof en>;

const it = {
  "design.title": "Design · {name}",
  "design.intro": "Scenario B: prima i requisiti, confermati da Lei; poi l'assistente ne ricava il protocollo.",
  "design.manual": "I passaggi nel manuale",
  "design.print.title": "{name} - Requisiti",
  "design.print.meta": "requisiti {status}",
  "design.print.confirmed": "confermati (versione {version}) {when}",
  "design.print.draft": "bozza, versione {version}",
  "design.by.ai": "scritti dall'assistente",
  "design.by.person": "modificati da una persona",
  "design.working": "L'assistente sta lavorando…",
  "design.saving": "Salvataggio…",
  "design.req.title": "1 · Requisiti",
  "design.req.confirmed": "Confermati · versione {version}",
  "design.req.draft": "Bozza · versione {version}",
  "design.req.print": "Stampa / PDF",
  "design.req.print.hint": "Stampare, o salvare come PDF dalla finestra di stampa",
  "design.req.confirm.hint": "L'assistente progetta il protocollo solo da requisiti confermati",
  "design.req.confirming": "Conferma in corso…",
  "design.req.confirm": "Conferma i requisiti",
  "design.req.empty": "Ancora nessun requisito. Descriva il dispositivo: cosa misura e con quale frequenza, quando dà allarme, come si configura, la radio e il budget energetico. Carichi prima schemi, datasheet e specifiche in Input nell'explorer - l'assistente li legge.",
  "design.req.placeholder": "Un sensore di riempimento per cestini pubblici: distanza a ultrasuoni ogni 15 minuti, invio ogni ora via LTE-M, allarme all'80 % di riempimento, batteria 5 anni con 2 x AA al litio, intervallo di invio e soglie configurabili…",
  "design.req.draftai": "Redigi i requisiti con l'assistente",
  "design.req.edit.note": "Markdown. Il salvataggio rende di nuovo i requisiti una bozza: li confermi dopo la modifica.",
  "design.req.older": "La versione {version} è stata confermata {when}; questa è una bozza più recente. Il protocollo resta com'è finché non conferma di nuovo.",
  "design.req.readthem": "Li legga, corregga ciò che è sbagliato (o lo dica all'assistente), risponda alle sue domande nel pannello dell'assistente, poi confermi. Prima non viene progettato nulla.",
  "design.proto.title": "2 · Protocollo",
  "design.proto.limit": "Limite del payload",
  "design.proto.bytes": "byte",
  "design.proto.download.hint": "I file .proto e le opzioni nanopb, per il team firmware",
  "design.proto.download": "Scarica per il firmware",
  "design.proto.ready": "I requisiti sono confermati: l'assistente può progettare il protocollo (.proto e opzioni nanopb) e poi classificarlo.",
  "design.proto.after": "Dopo che avrà confermato i requisiti.",
  "design.proto.design": "Progetta il protocollo con l'assistente",
  "design.proto.col.message": "Messaggio",
  "design.proto.col.largest": "Codifica massima",
  "design.proto.col.notes": "Note",
  "design.proto.root": "radice",
  "design.proto.unbounded": "illimitato",
  "design.proto.over": " · oltre il limite",
  "design.proto.callbacks": "trasmesso tramite callback (un elemento contato): {fields}",
  "design.proto.nosizes": "Dimensioni disponibili quando il manifest indica il messaggio radice.",
  "design.proto.problem": "opzioni nanopb {problem}",
  "design.proto.note": "Ogni campo string, bytes e repeated al suo limite nanopb; i numeri al loro varint più grande. Dimensioni dalla bozza attuale e dalle opzioni nanopb.",
  "design.opts.title": "3 · Opzioni nanopb (per il firmware)",
  "design.opts.meta": "versione {version} · {author} {when}",
  "design.opts.intro": "Una riga per ogni campo string, bytes o repeated:",
  "design.opts.kept": "Conservate nel progetto (il pacchetto dello store non ha posto per esse) e incluse nel download per il firmware.",
  "design.opts.by.ai": "scritte dall'assistente",
  "design.opts.by.person": "modificate da una persona",
  "design.opts.save": "Salva le opzioni",
  "design.opts.discard": "Scarta",
} satisfies Dict<typeof en>;

const fr = {
  "design.title": "Design · {name}",
  "design.intro": "Scénario B : d'abord les exigences, confirmées par vous ; ensuite l'assistant en tire le protocole.",
  "design.manual": "Les étapes dans le manuel",
  "design.print.title": "{name} - Exigences",
  "design.print.meta": "exigences {status}",
  "design.print.confirmed": "confirmées (version {version}) {when}",
  "design.print.draft": "brouillon, version {version}",
  "design.by.ai": "rédigées par l'assistant",
  "design.by.person": "modifiées par une personne",
  "design.working": "L'assistant travaille…",
  "design.saving": "Enregistrement…",
  "design.req.title": "1 · Exigences",
  "design.req.confirmed": "Confirmées · version {version}",
  "design.req.draft": "Brouillon · version {version}",
  "design.req.print": "Imprimer / PDF",
  "design.req.print.hint": "Imprimer, ou enregistrer en PDF depuis la boîte de dialogue d'impression",
  "design.req.confirm.hint": "L'assistant ne conçoit le protocole qu'à partir d'exigences confirmées",
  "design.req.confirming": "Confirmation…",
  "design.req.confirm": "Confirmer les exigences",
  "design.req.empty": "Pas encore d'exigences. Décrivez l'appareil : ce qu'il mesure et à quelle fréquence, ce qui déclenche une alarme, comment il se configure, sa radio et son budget énergétique. Chargez d'abord schémas, fiches techniques et spécifications sous Entrées dans l'explorateur - l'assistant les lit.",
  "design.req.placeholder": "Un capteur de remplissage pour poubelles publiques : distance par ultrasons toutes les 15 minutes, envoi toutes les heures via LTE-M, alarme à 80 % de remplissage, pile 5 ans avec 2 x AA lithium, intervalle d'envoi et seuils configurables…",
  "design.req.draftai": "Rédiger les exigences avec l'assistant",
  "design.req.edit.note": "Markdown. L'enregistrement refait des exigences un brouillon : confirmez-les après la modification.",
  "design.req.older": "La version {version} a été confirmée {when} ; ceci est un brouillon plus récent. Le protocole reste tel quel jusqu'à une nouvelle confirmation.",
  "design.req.readthem": "Lisez-les, corrigez ce qui est faux (ou dites-le à l'assistant), répondez à ses questions dans le panneau de l'assistant, puis confirmez. Rien n'est conçu avant.",
  "design.proto.title": "2 · Protocole",
  "design.proto.limit": "Limite du payload",
  "design.proto.bytes": "octets",
  "design.proto.download.hint": "Les fichiers .proto et les options nanopb, pour l'équipe firmware",
  "design.proto.download": "Télécharger pour le firmware",
  "design.proto.ready": "Les exigences sont confirmées : l'assistant peut concevoir le protocole (.proto et options nanopb) puis le classer.",
  "design.proto.after": "Après la confirmation des exigences.",
  "design.proto.design": "Concevoir le protocole avec l'assistant",
  "design.proto.col.message": "Message",
  "design.proto.col.largest": "Encodage maximal",
  "design.proto.col.notes": "Remarques",
  "design.proto.root": "racine",
  "design.proto.unbounded": "non borné",
  "design.proto.over": " · au-delà de la limite",
  "design.proto.callbacks": "transmis par callback (un élément compté) : {fields}",
  "design.proto.nosizes": "Tailles disponibles dès que le manifest nomme le message racine.",
  "design.proto.problem": "options nanopb {problem}",
  "design.proto.note": "Chaque champ string, bytes et repeated à sa limite nanopb ; les nombres à leur plus grand varint. Tailles tirées du brouillon actuel et des options nanopb.",
  "design.opts.title": "3 · Options nanopb (pour le firmware)",
  "design.opts.meta": "version {version} · {author} {when}",
  "design.opts.intro": "Une ligne par champ string, bytes ou repeated :",
  "design.opts.kept": "Conservées dans le projet (le package du store n'a pas de place pour elles) et incluses dans le téléchargement pour le firmware.",
  "design.opts.by.ai": "rédigées par l'assistant",
  "design.opts.by.person": "modifiées par une personne",
  "design.opts.save": "Enregistrer les options",
  "design.opts.discard": "Abandonner",
} satisfies Dict<typeof en>;

export default { en, de, it, fr };
